import { useState, useEffect } from "react";
import { Layers, FileText, TrendingUp, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { coverageUrl, qbFetch } from "./qb-cache";

interface CoverageItem {
  skill_id: string;
  skill_name: string;
  standard_code: string;
  grade: number;
  easy_count: number;
  medium_count: number;
  hard_count: number;
  total_problems: number;
  meets_launch_minimum: boolean;
}

function StatCard({
  label,
  value,
  icon: Icon,
  loading = false,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  loading?: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-1 p-3.5">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </span>
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        </div>
        {loading ? (
          <Skeleton className="h-7 w-16" />
        ) : (
          <div className="text-2xl font-bold tabular-nums">{value}</div>
        )}
      </CardContent>
    </Card>
  );
}

function deriveStatus(item: CoverageItem): "complete" | "partial" | "gap" {
  if (item.total_problems === 0) return "gap";
  if (item.meets_launch_minimum) return "complete";
  return "partial";
}

export function CoverageTracker() {
  const [gradeFilter, setGradeFilter] = useState("4");
  const [coverageData, setCoverageData] = useState<CoverageItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadCoverage() {
      setLoading(true);
      try {
        const data = await qbFetch<{ items: CoverageItem[] } | CoverageItem[]>(coverageUrl(`grade=${gradeFilter}`), { ttl: 60_000 });
        if (cancelled) return;
        const items = Array.isArray(data) ? data : (data.items || []);
        setCoverageData(items);
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch coverage data:", err);
        setCoverageData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadCoverage();
    return () => {
      cancelled = true;
    };
  }, [gradeFilter]);

  const totalSkills = coverageData.length;
  const totalProblems = coverageData.reduce((a, s) => a + s.total_problems, 0);
  const completeCount = coverageData.filter((s) => s.meets_launch_minimum).length;
  const gapCount = coverageData.filter((s) => s.total_problems === 0).length;
  const coveragePct = totalSkills > 0 ? Math.round((completeCount / totalSkills) * 100) : 0;

  return (
    <div className="min-w-0 space-y-5 overflow-hidden">
      <div>
        <h2 className="text-xl font-bold">Scale & Coverage</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Track curriculum coverage and identify gaps
        </p>
      </div>

      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Skills" value={String(totalSkills)} icon={Layers} loading={loading} />
        <StatCard label="Problems" value={totalProblems.toLocaleString()} icon={FileText} loading={loading} />
        <StatCard label="Coverage" value={`${coveragePct}%`} icon={TrendingUp} loading={loading} />
        <StatCard label="Gaps" value={String(gapCount)} icon={AlertCircle} loading={loading} />
      </div>

      <Card className="overflow-hidden">
        <CardContent className="overflow-hidden p-4">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold">
              Coverage — Grade {gradeFilter}
            </span>
            <div className="flex gap-1.5">
              {["3", "4", "5", "6"].map((g) => (
                <Button
                  key={g}
                  variant={gradeFilter === g ? "default" : "outline"}
                  size="sm"
                  onClick={() => setGradeFilter(g)}
                  className="text-xs"
                >
                  Grade {g}
                </Button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="min-w-0 overflow-hidden text-xs">
              <div className="grid items-center border-b" style={{ gridTemplateColumns: "minmax(0, 14%) minmax(0, 1fr) 36px 36px 36px 36px 60px" }}>
                <div className="py-2 px-1.5 text-left text-[10px] font-medium text-muted-foreground">Standard</div>
                <div className="py-2 px-1.5 text-left text-[10px] font-medium text-muted-foreground">Skill</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Mild</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Med</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Spicy</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Total</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Status</div>
              </div>
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="grid items-center border-b last:border-b-0" style={{ gridTemplateColumns: "minmax(0, 14%) minmax(0, 1fr) 36px 36px 36px 36px 60px" }}>
                  <div className="py-1.5 px-1.5"><Skeleton className="h-3 w-16" /></div>
                  <div className="py-1.5 px-1.5"><Skeleton className="h-3 w-3/4" /></div>
                  <div className="flex justify-center py-1.5"><Skeleton className="h-6 w-6 rounded" /></div>
                  <div className="flex justify-center py-1.5"><Skeleton className="h-6 w-6 rounded" /></div>
                  <div className="flex justify-center py-1.5"><Skeleton className="h-6 w-6 rounded" /></div>
                  <div className="flex justify-center py-1.5"><Skeleton className="h-3 w-4" /></div>
                  <div className="flex justify-center py-1.5"><Skeleton className="h-4 w-14 rounded" /></div>
                </div>
              ))}
            </div>
          ) : coverageData.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              No skills for Grade {gradeFilter}
            </div>
          ) : (
            <div className="min-w-0 overflow-hidden text-xs">
              {/* Header */}
              <div className="grid items-center border-b" style={{ gridTemplateColumns: "minmax(0, 14%) minmax(0, 1fr) 36px 36px 36px 36px 60px" }}>
                <div className="py-2 px-1.5 text-left text-[10px] font-medium text-muted-foreground">Standard</div>
                <div className="py-2 px-1.5 text-left text-[10px] font-medium text-muted-foreground">Skill</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Mild</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Med</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Spicy</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Total</div>
                <div className="py-2 px-1 text-center text-[10px] font-medium text-muted-foreground">Status</div>
              </div>
              {/* Rows */}
              {coverageData.map((s) => {
                const status = deriveStatus(s);
                return (
                  <div key={s.skill_id} className="grid items-center border-b last:border-b-0 hover:bg-muted/50" style={{ gridTemplateColumns: "minmax(0, 14%) minmax(0, 1fr) 36px 36px 36px 36px 60px" }}>
                    <div className="overflow-hidden py-1.5 px-1.5">
                      <span className="block truncate text-[9px] font-mono text-muted-foreground">{s.standard_code}</span>
                    </div>
                    <div className="overflow-hidden py-1.5 px-1.5">
                      <span className="block truncate text-xs">{s.skill_name}</span>
                    </div>
                    {[s.easy_count, s.medium_count, s.hard_count].map((v, i) => (
                      <div key={i} className="flex justify-center py-1.5">
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded text-[11px] font-bold ${
                            v >= 5
                              ? "bg-primary text-primary-foreground"
                              : v > 0
                                ? "bg-muted font-semibold"
                                : "bg-muted/50 text-muted-foreground/40"
                          }`}
                        >
                          {v}
                        </span>
                      </div>
                    ))}
                    <div className="py-1.5 text-center text-xs font-bold">{s.total_problems}</div>
                    <div className="flex justify-center py-1.5">
                      <Badge
                        variant={status === "complete" ? "default" : status === "gap" ? "destructive" : "outline"}
                        className="text-[9px]"
                      >
                        {status}
                      </Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
